import { useState, FormEvent, useEffect } from "react";

import moment from "moment";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Radio from "@mui/material/Radio";
import CloseIcon from "@mui/icons-material/Close";
import RadioGroup from "@mui/material/RadioGroup";
import FormControl from "@mui/material/FormControl";
import ApprovalIcon from "@mui/icons-material/Approval";
import FormControlLabel from "@mui/material/FormControlLabel";
import { Container, Typography } from "@mui/material";
import {
  Button,
  ButtonOwnProps,
  Dialog,
  Stack,
  TextField,
} from "@mui/material";

import conf from "config-dlm";
import SectionHeader from "components/utils/SectionHeader";

type RequestWithScheduleProps = {
  open: boolean;
  type: string;
  details: string | JSX.Element;
  onClose: () => void;
  onSubmit: (triggerAt: number, reason: string) => void;
};

const ScheduleView = (props: RequestWithScheduleProps) => {
  const { open, type, details, onClose, onSubmit } = props;

  const [date, setDate] = useState(moment());
  const [reason, setReason] = useState("");
  const [window, setWindow] = useState("now");
  const [locked, setLocked] = useState(false);

  const handleClose = () => onClose();
  const handleDateChange = (event: any) => setDate(moment(event.target.value));
  const handleWindowChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setWindow((event.target as HTMLInputElement).value);
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    let triggerAt;

    // if window set to 'now' send current date, else send input/textfield date
    if (window === "now") {
      triggerAt = moment().valueOf();
    } else {
      triggerAt = date.valueOf();
    }

    onSubmit(triggerAt, reason);

    setLocked(false);
  };

  // const viewMs = () => {
  //   if (window === "now") {
  //     console.log("date iso:", moment().toISOString());
  //     console.log("date in ms:", moment().valueOf());
  //   } else {
  //     console.log("date iso:", date.toISOString());
  //     console.log("date in ms:", moment(date).valueOf());
  //   }
  // };

  return (
    <Dialog maxWidth="md" fullWidth open={open} onClose={onClose}>
      <Container component="main" sx={conf.STYLE.DIALOG}>
        <SectionHeader
          key={`${type}-details-modal`}
          title={`Request: ${type}`}
          icon={<ApprovalIcon />}
          buttons={[
            <Button
              key={`${type}-details-close`}
              color="inherit"
              variant="outlined"
              onClick={handleClose}
            >
              <CloseIcon />
            </Button>,
          ]}
        />

        <Box component="form" noValidate onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Grid item xs={12}>
                <Typography>
                  <b>Details</b>
                </Typography>
              </Grid>

              <Grid container p={1} mt={1} border={1} borderRadius={1}>
                {details}
              </Grid>

              <Grid item xs={12} sx={{ mt: 2 }}>
                <Grid item xs={12}>
                  <Typography>
                    <b>Schedule</b>
                  </Typography>
                </Grid>

                <FormControl>
                  <RadioGroup
                    name={`${type}-window-group`}
                    value={window}
                    aria-labelledby={`${type}-window-group-label`}
                    onChange={handleWindowChange}
                  >
                    <FormControlLabel
                      value="now"
                      disabled={locked}
                      control={<Radio />}
                      label="As soon as possible"
                    />
                    <FormControlLabel
                      value="custom"
                      disabled={locked}
                      control={<Radio />}
                      label="Custom"
                    />
                  </RadioGroup>

                  <TextField
                    id="date-picker"
                    type="datetime-local"
                    value={date.format("YYYY-MM-DDTHH:mm")}
                    onChange={handleDateChange}
                    disabled={window === "now" || locked}
                    InputLabelProps={{ shrink: true }}
                    sx={{ ml: 4, mt: 0.5 }}
                  />
                </FormControl>

                <TextField
                  autoFocus
                  fullWidth
                  id="reason"
                  size="small"
                  type="reason"
                  margin="dense"
                  variant="standard"
                  label="Reason (required)"
                  value={reason}
                  disabled={locked}
                  onChange={(e) => setReason(e.target.value)}
                />
              </Grid>
            </Grid>
          </Grid>

          <Stack direction="row" justifyContent="flex-end" alignItems="center">
            <Button
              variant={locked ? "outlined" : "contained"}
              onClick={() => setLocked(!locked)}
              sx={{ mt: 3, mb: 2, mr: 2 }}
            >
              {locked ? "Cancel" : "Save"}
            </Button>

            <Button
              type="submit"
              variant="contained"
              disabled={!locked}
              sx={{ mt: 3, mb: 2 }}
            >
              Submit
            </Button>
          </Stack>
        </Box>
      </Container>
    </Dialog>
  );
};

const RequestWithSchedule = (props: {
  type: string;
  size?: ButtonOwnProps["size"];
  details?: any;
  disabled?: boolean;
  fullWidth?: boolean;
  onSuccess: boolean;
  onSubmit: (triggerAt: number, reason: string) => void;
}) => {
  const {
    type,
    details,
    size = "medium",
    disabled = false,
    fullWidth = false,
    onSubmit,
    onSuccess,
  } = props;

  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  useEffect(() => {
    if (onSuccess) handleClose();
  }, [onSuccess]);

  return (
    <div>
      <Button
        type="submit"
        size={size}
        color="secondary"
        variant="contained"
        onClick={handleOpen}
        disabled={disabled}
        fullWidth={fullWidth}
      >
        Request
      </Button>

      <ScheduleView
        open={open}
        type={type}
        details={details}
        onClose={handleClose}
        onSubmit={onSubmit}
      />
    </div>
  );
};

export default RequestWithSchedule;
